import { useRef, useState } from 'react'
import { Download, FileIcon, Loader2, Upload, X } from 'lucide-react'
import { useContractFiles, useUploadFile, downloadFile } from '../files/hooks'
import { useDocumentTypes } from '@/features/admin/document-types/hooks'
import { useContract } from '../hooks'
import { FileLightbox } from './FileLightbox'
import { FileImageThumb } from './FileImageThumb'
import type { FileAttachment } from '../files/types'
import type { DocumentType } from '@/features/admin/document-types/types'
import { useFieldDensity } from './field-renderer/FieldDensity'

type Props = {
  contractId: number
  documentTypeId: number
  /** Aktualna wartość pola - fileId odpowiadający rekordowi w files. */
  value: number | null
  /** Wywołane po udanym uploadzie albo "odpięciu" pliku. */
  onChange: (fileId: number | null) => void
  disabled?: boolean
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}

function formatUploadedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/**
 * Renderer pola FILE w formularzu umowy. Wgrany plik trafia do tabeli files.file_attachment
 * z documentTypeId wziętym z definicji pola - automatycznie pojawia się w zakładce Pliki.
 * Wartość pola to {@code fileId} (number) - wskaźnik do rekordu w files.
 *
 * "Usuwanie" ze pola tylko odpina referencję (extras = null); plik fizyczny
 * zostaje w S3 i widać go w FilesTab. Skasowanie definitywne robi user z FilesTab.
 */
export function FileFieldInput({ contractId, documentTypeId, value, onChange, disabled }: Props) {
  const density = useFieldDensity()
  const contractQ = useContract(contractId)
  const configurationCode = contractQ.data?.configurationCode
  const docTypesQ = useDocumentTypes(configurationCode, true)
  const filesQ = useContractFiles(contractId)
  const upload = useUploadFile(contractId)
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const docType: DocumentType | undefined = docTypesQ.data?.find(d => d.id === documentTypeId)
  const accepted = docType?.acceptedExtensions ?? null
  const file: FileAttachment | undefined = typeof value === 'number'
    ? filesQ.data?.find(f => f.id === value)
    : undefined

  const acceptAttr = accepted ? accepted : undefined

  // Osierocony zalacznik: plik tego typu istnieje (np. upload sie zapisal serwerowo,
  // ale link do pola nie - zerwana odpowiedz), a pole jest puste. Pokazujemy go z reczna
  // opcja "Podepnij". Celowo NIE linkujemy automatycznie - inaczej zlamalibysmy odpinanie
  // (przycisk X zostawia zalacznik w Plikach i czysci pole; auto-link by go natychmiast wrocil).
  const orphan: FileAttachment | undefined = !file
    ? filesQ.data?.find(f => f.documentTypeId === documentTypeId)
    : undefined
  const orphanNotice = orphan ? (
    <div className="flex flex-wrap items-center gap-1.5 rounded border border-amber-200 bg-amber-50/60 px-2 py-1 text-[11px] text-amber-800">
      <span>Plik tego typu jest już wgrany: <strong className="font-medium">{orphan.originalFilename}</strong></span>
      <button
        type="button"
        onClick={() => onChange(orphan.id)}
        className="font-medium underline hover:no-underline"
      >
        Podepnij do pola
      </button>
    </div>
  ) : null

  const validateExt = (filename: string): string | null => {
    if (!accepted) return null
    const lower = filename.toLowerCase()
    const allowed = accepted.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
    if (allowed.some(ext => lower.endsWith(ext))) return null
    return `Dozwolone: ${accepted}`
  }

  const handleFile = (f: File) => {
    setError(null)
    const validationErr = validateExt(f.name)
    if (validationErr) {
      setError(validationErr)
      return
    }
    upload.mutate(
      { file: f, documentTypeId },
      {
        onSuccess: (created) => onChange(created.id),
        onError: (e: unknown) => {
          const ax = e as { response?: { data?: { detail?: string } } }
          setError(ax.response?.data?.detail ?? 'Błąd uploadu')
        },
      },
    )
  }

  const onPick = () => inputRef.current?.click()

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (disabled || upload.isPending) return
    const f = e.dataTransfer.files?.[0]
    if (f) handleFile(f)
  }

  // Mamy już plik podpięty.
  if (file) {
    const isImg = file.contentType?.startsWith('image/')

    if (density === 'compact') {
      // Kompaktowy wiersz: miniaturka 40px + nazwa (truncate) + rozmiar + akcje ikonkowe.
      return (
        <>
          <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50/40 px-2 py-1.5 min-w-0">
            {isImg ? (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="shrink-0 w-10 h-10 rounded overflow-hidden border border-slate-200 bg-white"
                title="Podgląd"
              >
                <FileImageThumb
                  contractId={contractId}
                  fileId={file.id}
                  filename={file.originalFilename}
                  onClick={() => setLightboxOpen(true)}
                />
              </button>
            ) : (
              <div className="shrink-0 w-10 h-10 rounded border border-slate-200 bg-white flex items-center justify-center text-slate-400">
                <FileIcon size={18} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div
                className="text-xs font-medium text-slate-900 truncate"
                title={file.originalFilename}
              >
                {file.originalFilename}
              </div>
              <div className="text-[11px] text-slate-400">
                {formatBytes(file.sizeBytes)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => downloadFile(contractId, file.id)}
              className="shrink-0 p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-sky-700"
              title="Pobierz"
            >
              <Download size={13} />
            </button>
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="shrink-0 p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-700"
                title="Odepnij plik"
              >
                <X size={13} />
              </button>
            )}
          </div>
          {isImg && lightboxOpen && (
            <FileLightbox
              contractId={contractId}
              fileIds={[file.id]}
              filesMeta={filesQ.data ?? []}
              onClose={() => setLightboxOpen(false)}
            />
          )}
        </>
      )
    }

    // Comfortable - oryginalny wygląd.
    return (
      <>
        <div className="rounded-md border border-slate-200 bg-slate-50/40 px-2 py-2 flex items-center gap-3 h-28">
          {isImg ? (
            <FileImageThumb
              contractId={contractId}
              fileId={file.id}
              filename={file.originalFilename}
              onClick={() => setLightboxOpen(true)}
            />
          ) : (
            <div className="h-full aspect-square rounded bg-white border border-slate-200 flex items-center justify-center text-slate-500 shrink-0">
              <FileIcon size={28} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate" title={file.originalFilename}>
              {file.originalFilename}
            </div>
            <div className="text-xs text-slate-500">
              {formatBytes(file.sizeBytes)}
              {docType && <> · {docType.labelPl}</>}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Wgrano {formatUploadedAt(file.uploadedAt)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => downloadFile(contractId, file.id)}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-sky-700"
            title="Pobierz"
          >
            <Download size={14} />
          </button>
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="p-1.5 rounded hover:bg-rose-50 text-slate-600 hover:text-rose-700"
              title="Odepnij plik (zostanie w zakładce Pliki)"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {isImg && lightboxOpen && (
          <FileLightbox
            contractId={contractId}
            fileIds={[file.id]}
            filesMeta={filesQ.data ?? []}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </>
    )
  }

  // Empty state.
  if (density === 'compact') {
    // Kompaktowy wariant: mały przycisk "Wgraj plik" zamiast dużego dropzone.
    return (
      <div className="flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          className="hidden"
          disabled={disabled || upload.isPending}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handleFile(f)
            e.target.value = ''
          }}
        />
        <button
          type="button"
          onClick={onPick}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          disabled={disabled || upload.isPending}
          className={[
            'inline-flex items-center gap-1.5 self-start rounded border px-2.5 py-1 text-xs font-medium transition-colors',
            'text-slate-600 hover:text-sky-700 disabled:opacity-60 disabled:cursor-not-allowed',
            dragOver
              ? 'border-sky-400 bg-sky-50/50 text-sky-700'
              : 'border-slate-300 bg-white hover:bg-slate-50',
          ].join(' ')}
        >
          {upload.isPending ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Upload size={13} />
          )}
          {upload.isPending ? 'Wgrywanie...' : 'Wgraj plik'}
        </button>
        {accepted && (
          <span className="text-[10px] text-slate-400">Dozwolone: {accepted}</span>
        )}
        {orphanNotice}
        {error && <span className="text-xs text-rose-600">{error}</span>}
      </div>
    )
  }

  // Comfortable empty - oryginalny dropzone.
  return (
    <div className="flex flex-col gap-1">
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        disabled={disabled || upload.isPending}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) handleFile(f)
          e.target.value = ''
        }}
      />
      <button
        type="button"
        onClick={onPick}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        disabled={disabled || upload.isPending}
        className={[
          'rounded-md border-2 border-dashed px-4 flex flex-col items-center justify-center gap-1.5 transition-colors h-28',
          'text-sm text-slate-500 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed',
          dragOver ? 'border-sky-400 bg-sky-50/50' : 'border-slate-300 bg-white',
        ].join(' ')}
      >
        {upload.isPending ? (
          <>
            <Loader2 size={20} className="animate-spin text-sky-600" />
            <span>Wgrywanie...</span>
          </>
        ) : (
          <>
            <Upload size={20} className="text-slate-400" />
            <span><span className="font-medium text-slate-700">Kliknij aby wybrać</span> lub przeciągnij plik</span>
            {accepted && <span className="text-xs text-slate-400">Dozwolone: {accepted}</span>}
          </>
        )}
      </button>
      {orphanNotice}
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  )
}
